import { CreateAccountVendorPreferenceRequest } from '@zola/svc-marketplace-ts-types';

import { error as errorNotification } from '~/actions/notificationActions';
import type { AppThunk } from '~/reducers';
import {
  AccountVendorPreferencesView,
  AccountWeddingPreferencesView,
  UpdateAccountVendorPreferenceRequest,
  SaveAccountWeddingPreferencesRequest,
  CreateAddressRequest,
  UpdateWeddingLocationRequest,
  PageOriginEnum,
  BoardSourceEnum,
} from '~/types/responseTypes';
import ApiService from '~/util/apiService';
import Logger from '~/util/logger';

import {
  UPDATED_VENDOR_PREFERENCES,
  RECEIVED_VENDOR_PREFERENCES,
  RECEIVED_WEDDING_PREFERENCES,
  REQUESTING_VENDOR_PREFERENCES,
  REQUESTING_WEDDING_PREFERENCES,
  RequestingWeddingPreferencesAction,
  RequestingVendorPreferencesAction,
  ReceivedVendorPreferencesAction,
  ReceivedWeddingPreferencesAction,
  UpdatedVendorPreferencesAction,
  UPDATING_WEDDING_LOCATION,
  UPDATED_WEDDING_LOCATION,
  UpdatedWeddingLocationAction,
  UpdatingWeddingLocationAction,
} from './types/accountPreferenceActionTypes';

const requestingWeddingPreferences = (): RequestingWeddingPreferencesAction => ({
  type: REQUESTING_WEDDING_PREFERENCES,
});
const requestingVendorPreferences = (): RequestingVendorPreferencesAction => ({
  type: REQUESTING_VENDOR_PREFERENCES,
});
export const receivedWeddingPreferences = (
  response: AccountWeddingPreferencesView
): ReceivedWeddingPreferencesAction => ({
  type: RECEIVED_WEDDING_PREFERENCES,
  payload: response,
});
const receivedVendorPreferences = (
  response: AccountVendorPreferencesView[]
): ReceivedVendorPreferencesAction => ({
  type: RECEIVED_VENDOR_PREFERENCES,
  payload: response,
});

export const updatedVendorPreferences = (
  response: AccountVendorPreferencesView
): UpdatedVendorPreferencesAction => ({
  type: UPDATED_VENDOR_PREFERENCES,
  payload: response,
});

const updatingWeddingLocation = (): UpdatingWeddingLocationAction => ({
  type: UPDATING_WEDDING_LOCATION,
});
const updatedWeddingLocations = (
  response: AccountWeddingPreferencesView
): UpdatedWeddingLocationAction => ({
  type: UPDATED_WEDDING_LOCATION,
  payload: response,
});

export const updateWeddingLocation = (
  address: CreateAddressRequest,
  source?: BoardSourceEnum,
  origin?: PageOriginEnum
): AppThunk<Promise<void>> => {
  return (dispatch) => {
    dispatch(updatingWeddingLocation());
    return ApiService.put<AccountWeddingPreferencesView, UpdateWeddingLocationRequest>(
      `/web-marketplace-api/v1/account-preferences/wedding-location`,
      { address, source, origin }
    ).then((response) => {
      dispatch(updatedWeddingLocations(response));
    });
  };
};

export const updateVendorPreferences = (
  uuid: string | null,
  values: UpdateAccountVendorPreferenceRequest
): AppThunk<Promise<void | UpdatedVendorPreferencesAction>> => {
  return (dispatch) => {
    return putVendorPreferences(uuid, values)
      .then((response: AccountVendorPreferencesView) =>
        dispatch(updatedVendorPreferences(response))
      )
      .catch((error: Error) => {
        Logger.error(error);
        dispatch(errorNotification({ message: 'Error updating your vendor preferences.' }));
      });
  };
};

export const putVendorPreferences = (
  uuid: string | null,
  values: UpdateAccountVendorPreferenceRequest
) => {
  return ApiService.put(`/web-marketplace-api/v1/vendor-preferences/${uuid}`, values);
};

export const fetchVendorPreferences = () =>
  ApiService.get<AccountVendorPreferencesView[]>('/web-marketplace-api/v1/vendor-preferences');

export const createVendorPreferences = (body: CreateAccountVendorPreferenceRequest) =>
  ApiService.post('/web-marketplace-api/v1/vendor-preferences', body);

export const getVendorPreferences = (): AppThunk<
  Promise<void | ReceivedVendorPreferencesAction>
> => {
  return (dispatch) => {
    dispatch(requestingVendorPreferences());
    return fetchVendorPreferences()
      .then((response: AccountVendorPreferencesView[]) =>
        dispatch(receivedVendorPreferences(response))
      )
      .catch((error: Error) => Logger.error(error));
  };
};

export const getWeddingPreferences = (): AppThunk<Promise<AccountWeddingPreferencesView>> => {
  return (dispatch) => {
    dispatch(requestingWeddingPreferences());
    return fetchWeddingPreferences().then((response) => {
      dispatch(receivedWeddingPreferences(response));
      return response;
    });
  };
};
export function fetchWeddingPreferences() {
  return ApiService.get<AccountWeddingPreferencesView>(
    '/web-marketplace-api/v1/wedding-preferences'
  );
}

export function putWeddingPreferences(
  uuid: string | null,
  values: SaveAccountWeddingPreferencesRequest
) {
  return ApiService.put<AccountWeddingPreferencesView, SaveAccountWeddingPreferencesRequest>(
    `/web-marketplace-api/v1/wedding-preferences/${uuid}`,
    values
  );
}
export function postWeddingPreferences(values: SaveAccountWeddingPreferencesRequest) {
  return ApiService.post<AccountWeddingPreferencesView, SaveAccountWeddingPreferencesRequest>(
    `/web-marketplace-api/v1/wedding-preferences`,
    values
  );
}
